import {
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepickerIntl } from '@angular/material/datepicker';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs';
import { SubSink } from 'subsink';

import {
  VipDiscountDto,
  VipDiscountToMerchantDto,
  ExistingVipDiscountsRequest,
} from '@app/api/models';
import { cancellationMockDate } from '@shared/constants/cancellation-mock-date';
import { dateToParam } from '@shared/helpers/date-to-param.helper';

import { ProductVipDiscountToMerchant } from '../../interfaces/product-discount-to-merchant.interface';
import {
  VipDiscountsSellingActions,
  VipDiscountsSellingSelectors,
} from '../../store/vip-discounts-selling.slice';
import { VipDiscountsSettingsTableComponent } from './vip-discounts-settings-table/vip-discounts-settings-table.component';

@Component({
  selector: 'app-vip-discounts-settings',
  templateUrl: './vip-discounts-settings.component.html',
  styleUrls: ['./vip-discounts-settings.component.scss'],
})
export class VipDiscountsSettingsComponent implements OnInit, OnDestroy {
  @ViewChildren(VipDiscountsSettingsTableComponent)
  discountComponents!: QueryList<VipDiscountsSettingsTableComponent>;
  discountToMerchantList$ = this.store.select(
    VipDiscountsSellingSelectors.selectVipDiscounts
  );

  isAllMerchantsSelected$ = this.store.select(
    VipDiscountsSellingSelectors.isAllMerchantsSelected
  );

  merchantIdList?: Array<string>;

  subs = new SubSink();

  discountModeOptions = [
    { label: 'VIP_DISCOUNTS.PERCENT', value: 'PERCENT' },
    { label: 'VIP_DISCOUNTS.AMOUNT', value: 'AMOUNT' },
    { label: 'VIP_DISCOUNTS.FIX_PRICE', value: 'FIX_PRICE' },
  ];

  today = new Date();

  form = new FormGroup({
    discountValue: new FormControl(null, [
      Validators.required,
      Validators.min(0.0001),
    ]),
    discountMode: new FormControl('PERCENT'),
    startDate: new FormControl(new Date()),
    endDate: new FormControl(''),
  });

  constructor(
    private store: Store,
    public datePicker: MatDatepickerIntl,
    private translateService: TranslateService
  ) {
    // TODO translate
    // datePicker.closeCalendarLabel = this.translateService.instant(
    //   'SHARED.CLOSE_CALENDAR'
    // );
    datePicker.closeCalendarLabel = 'Naptár bezárása';
    this.store.dispatch(
      VipDiscountsSellingActions.clearSelectedProductsForGroupOperation.trigger()
    );
  }

  ngOnInit(): void {
    this.subs.add(
      this.discountToMerchantList$.pipe(first()).subscribe((discounts) => {
        this.merchantIdList = discounts.map(
          (discount) => discount.merchant.merchantId!
        );
        const request: ExistingVipDiscountsRequest = {
          toMerchantIdList: this.merchantIdList,
          packagingUnitIdList: discounts[0]?.vipDiscountList.map(
            (product) => product.packagingUnitId!
          ),
        };
        if (
          request.packagingUnitIdList?.length &&
          request.toMerchantIdList?.length
        ) {
          this.store.dispatch(
            VipDiscountsSellingActions.findExistingDiscountCombinations.trigger(
              {
                request: request,
              }
            )
          );
        }
      })
    );

    this.subs.add(
      this.store
        .select(VipDiscountsSellingSelectors.selectExistingDiscountCombinations)
        .subscribe((response) => {
          if (
            Object.values(response).length &&
            response.merchantIdDiscountListMap
          ) {
            Object.keys(response.merchantIdDiscountListMap).forEach(
              (merchant, idx) => {
                const index =
                  this.merchantIdList?.findIndex(
                    (merchantId) => merchantId === merchant
                  ) ?? -1;
                if (index > -1) {
                  this.discountComponents
                    .toArray()
                    .at(index)
                    ?.onData(
                      Object.values(response.merchantIdDiscountListMap ?? {})[
                        idx
                      ]
                    );
                }
              }
            );
          }
        })
    );
  }

  discountModeChanged() {
    if (this.form.get('discountMode')?.value === 'PERCENT') {
      this.form.get('discountValue')?.addValidators(Validators.max(99));
    } else {
      this.form.get('discountValue')?.removeValidators(Validators.max(99));
    }
    this.form
      .get('discountValue')
      ?.setValidators(
        this.form.get('discountMode')?.value === 'PERCENT'
          ? [Validators.required, Validators.min(0.0001), Validators.max(99)]
          : [Validators.required, Validators.min(0.0001)]
      );
    this.form.get('discountValue')?.updateValueAndValidity();
  }

  backButtonClicked() {
    history.back();
  }
  applyGroupDiscount() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.discountComponents.forEach((component) => {
        component.updateFormByGroupDiscountOperation(
          this.form.value as VipDiscountDto
        );
      });
    }
  }

  deleteSelectedItems() {
    this.discountComponents.forEach((component) =>
      component.deleteSelectedDiscounts()
    );
  }

  trackDiscount(index: number, discount: ProductVipDiscountToMerchant) {
    return discount.vipDiscountList.at(index);
  }

  onSave() {
    this.discountComponents.forEach((component) => {
      component.markFormTouched();
    });
    if (
      this.discountComponents
        .toArray()
        .every((component) => component.formGroup.valid)
    ) {
      const vipDiscountToMerchantList: Array<VipDiscountToMerchantDto> =
        this.discountComponents
          .toArray()
          .map((component) => structuredClone(component.formGroup.value));
      vipDiscountToMerchantList.forEach((discountToMerchant) => {
        discountToMerchant.vipDiscountList.map((item: VipDiscountDto) => {
          item.startDate = item.startDate
            ? dateToParam(item.startDate)
            : dateToParam(new Date());
          item.endDate = item.endDate
            ? dateToParam(item.endDate)
            : dateToParam(new Date(cancellationMockDate));
          return item;
        });
      });
      this.store.dispatch(
        VipDiscountsSellingActions.saveDiscounts.trigger({
          request: { vipDiscountToMerchantList },
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
